import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Search,
  Inventory,
  AttachMoney,
  Warning,
  TrendingUp,
} from "@mui/icons-material";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import CountUp from "react-countup";
import { getMainDashboardCounts } from "../../../Services/Services";
import { toast } from "react-toastify";

const COLORS = ["#3f51b5", "#f50057", "#ff9800", "#4caf50", "#9c27b0"];

const DashboardPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch data from API
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await getMainDashboardCounts();
      // response is already the JSON object you showed
      setDashboardData(response);
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Failed to load dashboard data");
      // Optionally set fallback data or keep null
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Format currency
  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);

  // If still loading
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  // If no data or error
  if (!dashboardData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">No data available. Please check API connection.</Typography>
      </Box>
    );
  }

  // Destructure data with fallbacks
  const {
    summary = {},
    stockByCategory = [],
    stockStatus = {},
    monthlyTrend = [],
    items = [],
  } = dashboardData;

  // Filter items based on search (name or SKU)
  const filteredItems = items.filter(
    (item) =>
      (item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.sku && item.sku.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const paginatedItems = filteredItems.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // KPI cards data from API summary
  const kpiCards = [
    {
      title: "Total Items",
      value: summary.totalItems || 0,
      sub: "All Items",
      icon: <Inventory sx={{ fontSize: 32 }} />,
      gradient: "linear-gradient(135deg, #1976d2, #64b5f6)",
    },
    {
      title: "Total Stock (Qty)",
      value: summary.totalStockQty || 0,
      sub: "All Warehouses",
      icon: <Inventory sx={{ fontSize: 32 }} />,
      gradient: "linear-gradient(135deg, #2e7d32, #81c784)",
    },
    {
      title: "Total Inventory Value",
      value: summary.totalInventoryValue || 0,
      sub: "All Items Value",
      icon: <AttachMoney sx={{ fontSize: 32 }} />,
      gradient: "linear-gradient(135deg, #ed6c02, #ffb74d)",
      isCurrency: true,
    },
    {
      title: "Low Stock Items",
      value: summary.lowStockItems || 0,
      sub: "Check Items",
      icon: <Warning sx={{ fontSize: 32 }} />,
      gradient: "linear-gradient(135deg, #d32f2f, #ef9a9a)",
    },
  ];

  return (
    <Box sx={{minHeight: "100vh" }}>
   

      {/* KPI Cards Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {kpiCards.map((card, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <Card
              sx={{
                background: card.gradient,
                color: "white",
                borderRadius: 4,
                transition: "transform 0.25s ease, box-shadow 0.25s ease",
                "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 24px -8px rgba(0,0,0,0.2)" },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.85, fontWeight: 500 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                      <CountUp
                        end={card.value}
                        duration={1.5}
                        separator=","
                        prefix={card.isCurrency ? "₹" : ""}
                      />
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.75, fontWeight: 500 }}>
                      {card.sub}
                    </Typography>
                  </Box>
                  {card.icon}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Secondary metrics: Raw Material & Finished Goods Value */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <Card sx={{ borderRadius: 4, height: "100%", boxShadow: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Raw Material Value
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="#2e7d32">
                ₹{formatCurrency(summary.rawMaterialValue)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Value
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card sx={{ borderRadius: 4, height: "100%", boxShadow: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Finished Goods Value
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="#ed6c02">
                ₹{formatCurrency(summary.finishedGoodsValue)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Value
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row */}
    {/* Stock Value by Category - Horizontal Scrollable Bar Chart */}
<Grid item xs={12} md={5}>
  <Card sx={{ p: 2, borderRadius: 4, boxShadow: 2, height: "100%", overflow: "hidden" }}>
    <Typography variant="h6" fontWeight="600" gutterBottom>
      Stock Value by Category
    </Typography>
    {stockByCategory.length > 0 ? (
      <Box sx={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
        <Box sx={{ minWidth: Math.max(stockByCategory.length * 120, 300), height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={stockByCategory}
              margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                interval={0}
                height={60}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
              />
              <Tooltip
                formatter={(value) => `₹${formatCurrency(value)}`}
                labelFormatter={(label) => `Category: ${label}`}
              />
              <Bar
                dataKey="value"
                fill="#3f51b5"
                radius={[6, 6, 0, 0]}
                barSize={50}
              >
                {stockByCategory.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    ) : (
      <Typography sx={{ textAlign: "center", py: 10 }} color="text.secondary">
        No category data available
      </Typography>
    )}
  </Card>
</Grid>

      {/* Items Table with Search & Pagination */}
      <Card sx={{ borderRadius: 4, overflow: "hidden", boxShadow: 3 }}>
        <Box
          sx={{
            p: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            borderBottom: "1px solid #eaeef2",
          }}
        >
          <Typography variant="h6" fontWeight="700">
            Inventory Items
          </Typography>
          <TextField
            size="small"
            placeholder="Search item name, SKU, code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 260, bgcolor: "#fff", borderRadius: 2 }}
          />
        </Box>

        <TableContainer component={Paper} elevation={0}>
          <Table stickyHeader size={isMobile ? "small" : "medium"} sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell sx={{ fontWeight: 700, borderBottom: "2px solid #e0e4e8" }}>S. No.</TableCell>
                <TableCell sx={{ fontWeight: 700, borderBottom: "2px solid #e0e4e8" }}>Item Name / SKU</TableCell>
                <TableCell sx={{ fontWeight: 700, borderBottom: "2px solid #e0e4e8" }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700, borderBottom: "2px solid #e0e4e8" }}>Product Type</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, borderBottom: "2px solid #e0e4e8" }}>Current Stock</TableCell>
                <TableCell sx={{ fontWeight: 700, borderBottom: "2px solid #e0e4e8" }}>Unit</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, borderBottom: "2px solid #e0e4e8" }}>Price (₹)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, borderBottom: "2px solid #e0e4e8" }}>Unit Price (₹)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedItems.map((item, idx) => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{
                    backgroundColor: idx % 2 === 0 ? "#fafafa" : "#ffffff",
                    "&:hover": { backgroundColor: "#e3f2fd", transition: "0.2s" },
                  }}
                >
                  <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="500">
                        {item.name?.length > 60 ? item.name.slice(0, 60) + "..." : item.name || "N/A"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        SKU: {item.sku || "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.category || "N/A"}
                      size="small"
                      sx={{ bgcolor: "#e3f2fd", fontWeight: 500, fontSize: "0.75rem" }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.productType || "N/A"}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 500, fontSize: "0.75rem" }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      fontWeight={item.currentStock < 5 ? "bold" : "normal"}
                      color={item.currentStock < 5 ? "error" : "inherit"}
                    >
                      {item.currentStock}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.unit || "-"}</TableCell>
                  <TableCell align="right">₹{formatCurrency(item.price)}</TableCell>
                  <TableCell align="right">₹{formatCurrency(item.unitPrice)}</TableCell>
                </TableRow>
              ))}
              {paginatedItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No matching items found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            p: 2,
            borderTop: "1px solid #eaeef2",
            bgcolor: "#fff",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Showing {page * rowsPerPage + 1} to{" "}
            {Math.min((page + 1) * rowsPerPage, filteredItems.length)} of {filteredItems.length} entries
          </Typography>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredItems.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Show entries"
          />
        </Box>
      </Card>
    </Box>
  );
};

export default DashboardPage;